import React, { useState, useRef, useEffect } from "react";
import { Box, TextField } from "@mui/material";
import { useTheme } from "../../theme/ThemeContext";

const OTPInput = ({ value, onChange, error, disabled = false }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const inputElementRefs = useRef([]);
  const isPastingRef = useRef(false);
  const theme = useTheme();
  useEffect(() => {
    if (value) {
      const otpArray = value.split("").slice(0, 6);
      const newOtp = [...otpArray, ...Array(6 - otpArray.length).fill("")];
      setOtp(newOtp);
    }
  }, [value]);

  const handleChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1); // Take only the last character
    }

    const newOtp = [...otp];
    const previousValue = newOtp[index];
    newOtp[index] = value;
    setOtp(newOtp);

    // Call parent onChange with complete OTP
    const completeOtp = newOtp.join("");
    onChange(completeOtp);

    // Auto-focus next input when a digit is entered (whether field was empty or had a value)
    // This handles both: typing in empty field and replacing existing value
    // Don't move focus if we're in the middle of a paste operation
    if (value && value.trim() !== "" && index < 5 && !isPastingRef.current) {
      // Use a small delay to ensure the current input is processed
      setTimeout(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          // Select all text in the next input for easy replacement
          const inputElement = inputElementRefs.current[index + 1];
          if (inputElement) {
            inputElement.select();
          }
        }
      }, 10);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" || e.keyCode === 8) {
      const currentInputElement = inputElementRefs.current[index];
      if (!currentInputElement) return;

      const inputValue = currentInputElement.value || "";
      const stateValue = otp[index] || "";

      const isEmpty = inputValue === "" && stateValue === "";

      if (isEmpty && index > 0) {
        e.preventDefault();
        e.stopPropagation();

        // Focus the previous input element directly
        const prevInputElement = inputElementRefs.current[index - 1];
        if (prevInputElement) {
          setTimeout(() => {
            prevInputElement.focus();
            const length = prevInputElement.value?.length || 0;
            if (prevInputElement.setSelectionRange) {
              prevInputElement.setSelectionRange(length, length);
            }
          }, 0);
        }
      }
    } else if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
      const currentInputElement = inputElementRefs.current[index];
      if (currentInputElement && currentInputElement.value && index < 5) {
        e.preventDefault();
        const newValue = e.key;
        const newOtp = [...otp];
        newOtp[index] = newValue;
        setOtp(newOtp);

        const completeOtp = newOtp.join("");
        onChange(completeOtp);

        setTimeout(() => {
          const nextInput = inputRefs.current[index + 1];
          if (nextInput) {
            nextInput.focus();
            const nextInputElement = inputElementRefs.current[index + 1];
            if (nextInputElement) {
              nextInputElement.select();
            }
          }
        }, 10);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    isPastingRef.current = true;
    const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
    const otpArray = pastedData.split("").slice(0, 6);
    const newOtp = [...otpArray, ...Array(6 - otpArray.length).fill("")];
    setOtp(newOtp);
    onChange(pastedData);

    // Focus on the last filled input field (or last field if all are filled)
    // Use requestAnimationFrame to ensure state and DOM have updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        const lastFilledIndex = Math.min(otpArray.length - 1, 5);
        const lastInputElement = inputElementRefs.current[lastFilledIndex];
        if (lastInputElement) {
          lastInputElement.focus();
          const length = lastInputElement.value?.length || 0;
          if (lastInputElement.setSelectionRange) {
            lastInputElement.setSelectionRange(length, length);
          }
        }
        // Reset the paste flag after focusing
        isPastingRef.current = false;
      }, 0);
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: 0.5, sm: 0.75, md: 1, lg: 1 },
        justifyContent: "center",
        alignItems: "center",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        px: { xs: 1, sm: 0 },
      }}
    >
      {otp.map((digit, index) => (
        <TextField
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onPaste={handlePaste}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: "center",
              fontWeight: 600,
            },
            ref: (el) => (inputElementRefs.current[index] = el),
            onKeyDown: (e) => handleKeyDown(index, e),
          }}
          sx={{
            width: { xs: "40px", sm: "45px", md: "50px", lg: "52px" },
            height: { xs: "50px", sm: "55px", md: "60px", lg: "62px" },
            "& .MuiOutlinedInput-root": {
              "&:hover fieldset": {
                borderColor: theme?.app_button_color,
              },
              "&:-webkit-autofill": {
                "-webkit-box-shadow": "0 0 0 100px white inset",
                "-webkit-text-fill-color": theme?.text_color,
              },
              "& input": {
                fontSize: { xs: "1.2rem", sm: "1.35rem", md: "1.5rem", lg: "1.6rem" },
                padding: { xs: "8px 4px", sm: "10px 4px", md: "12px 4px", lg: "12px 4px" },
              },
            },
          }}
          autoComplete="off"
        />
      ))}
    </Box>
  );
};

export default OTPInput;
